import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'motion/react';
import { useParams, useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { ChevronLeft, Clock, Share2, Twitter, Link2, Check, ArrowRight } from 'lucide-react';
import { fieldNotes, FieldNote as JourneyPostType } from '../data/fieldNotes';
import { useSEO } from '../hooks/useSEO';
import Breadcrumb from './Breadcrumb';
import SiteNav from './SiteNav';
import SiteFooter from './SiteFooter';

const FIELD_NOTES_STYLES = `
.prose-field-notes {
  font-family: var(--font-serif);
  font-size: 1.125rem;
  line-height: 1.9;
  color: rgba(26,26,26,0.75);
  max-width: 680px;
  margin-left: auto;
  margin-right: auto;
}
@media (min-width: 768px) {
  .prose-field-notes { font-size: 1.1875rem; }
}
.dark .prose-field-notes {
  color: rgba(255,255,255,0.70);
}
.prose-field-notes p {
  margin-bottom: 2em;
}
.prose-field-notes h2 {
  font-family: var(--font-display);
  font-size: 2rem;
  font-weight: 800;
  color: #1A1A1A;
  letter-spacing: -0.03em;
  margin-top: 2.5em;
  margin-bottom: 0.8em;
  line-height: 1.15;
}
.dark .prose-field-notes h2 {
  color: #FFFFFF;
}
.prose-field-notes h3 {
  font-family: var(--font-display);
  font-size: 1.25rem;
  font-weight: 700;
  color: rgba(26,26,26,0.9);
  margin-top: 2.5em;
  margin-bottom: 0.75em;
}
.dark .prose-field-notes h3 {
  color: rgba(255,255,255,0.9);
}
.prose-field-notes hr {
  display: none;
}
.prose-field-notes blockquote {
  border-left: 3px solid #C5A059;
  padding: 0 0 0 1.5em;
  margin: 2.5em 0;
  font-style: italic;
  font-size: 1.1875rem;
  line-height: 1.75;
  color: rgba(26,26,26,0.55);
}
.dark .prose-field-notes blockquote {
  color: rgba(255,255,255,0.50);
}
.prose-field-notes blockquote p {
  margin-bottom: 0;
}
.prose-field-notes a {
  color: #9B6F1A;
  text-decoration: underline;
  text-underline-offset: 4px;
  text-decoration-thickness: 2px;
  text-decoration-color: #9B6F1A;
  font-weight: 600;
  transition: color 0.2s, background 0.2s;
}
.dark .prose-field-notes a {
  color: #F0C75E;
  text-decoration-color: #F0C75E;
}
.prose-field-notes a:hover {
  color: #1A1A1A;
  background: rgba(155,111,26,0.1);
  border-radius: 2px;
}
.dark .prose-field-notes a:hover {
  color: #FFFFFF;
  background: rgba(240,199,94,0.15);
}
.prose-field-notes strong {
  color: #1A1A1A;
  font-weight: 600;
}
.dark .prose-field-notes strong {
  color: #FFFFFF;
}
.prose-field-notes em {
  font-style: italic;
  color: #C5A059;
}
.prose-field-notes ul {
  padding-left: 0;
  margin: 1.75em 0;
  list-style-type: none;
}
.prose-field-notes ul li {
  position: relative;
  padding-left: 1.5em;
  margin-bottom: 1.1em;
  line-height: 1.8;
}
.prose-field-notes ul li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0.7em;
  width: 6px;
  height: 1px;
  background: #C5A059;
  opacity: 0.5;
}
.prose-field-notes img {
  max-width: 100%;
  height: auto;
  border-radius: 0.25rem;
  margin: 2.5em 0;
}
.prose-field-notes .pull-quote {
  font-family: var(--font-serif);
  font-size: 1.5rem;
  line-height: 1.6;
  font-style: italic;
  text-align: center;
  padding: 2em 1em;
  margin: 2.5em 0;
  border-top: 1px solid rgba(197,160,89,0.2);
  border-bottom: 1px solid rgba(197,160,89,0.2);
  color: rgba(26,26,26,0.6);
}
.dark .prose-field-notes .pull-quote {
  color: rgba(255,255,255,0.55);
}
@media (min-width: 768px) {
  .prose-field-notes .pull-quote {
    font-size: 1.625rem;
    padding: 2.5em 2em;
    margin: 3em -2em;
  }
}
`;

const JourneyPostPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  // Intercept internal link clicks in HTML content to use React Router
  const handleContentClick = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    const anchor = target.closest('a');
    if (!anchor) return;
    const href = anchor.getAttribute('href');
    if (href && href.startsWith('/') && !href.startsWith('//')) {
      e.preventDefault();
      navigate(href);
    }
  }, [navigate]);

  const post = fieldNotes.find((p) => p.slug === slug);

  useSEO({
    title: post ? `${post.title} — Aura Traveler Field Notes` : 'Journey Not Found | Aura Traveler',
    description: post?.excerpt || 'Explore curated travel stories from Aura Traveler.',
    canonical: post ? `https://www.auratraveler.com/field-notes/${post.slug}` : undefined,
    ogImage: post?.heroImage,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return (
      <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-display text-3xl text-[#1A1A1A] mb-4">Story Not Found</h1>
          <p className="text-[#1A1A1A]/50 font-serif italic mb-8">This journey hasn't been written yet.</p>
          <button
            onClick={() => navigate('/field-notes')}
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#1A1A1A] text-white text-[11px] font-semibold uppercase tracking-[0.15em] rounded-full hover:opacity-80 transition-opacity"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
            Back to Field Notes
          </button>
        </div>
      </div>
    );
  }

  const relatedPosts = post.relatedSlugs
    .map((s) => fieldNotes.find((p) => p.slug === s))
    .filter((p): p is JourneyPostType => p !== undefined);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: do nothing
    }
  };

  const handleTwitterShare = () => {
    const url = encodeURIComponent(window.location.href);
    const text = encodeURIComponent(post.title);
    window.open(`https://twitter.com/intent/tweet?url=${url}&text=${text}`, '_blank');
  };

  // Article schema for field notes
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.excerpt,
    image: post.heroImage,
    author: { '@type': 'Organization', 'name': 'Aura Traveler' },
    publisher: { '@type': 'Organization', 'name': 'Aura Traveler', url: 'https://www.auratraveler.com' },
    datePublished: post.date,
    mainEntityOfPage: { '@type': 'WebPage', '@id': `https://www.auratraveler.com/field-notes/${post.slug}` },
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5] text-[#1A1A1A]">
      <SiteNav />
      <style dangerouslySetInnerHTML={{ __html: FIELD_NOTES_STYLES }} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      {/* Breadcrumb */}
      <div className="fixed top-0 left-0 right-0 z-50 px-4 sm:px-6 md:px-12 py-4 bg-black/50 backdrop-blur-md">
        <Breadcrumb items={[
          { label: 'Field Notes', href: '/field-notes' },
          { label: post.title },
        ]} />
      </div>

      {/* Hero Image */}
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="relative h-[60vh] md:h-[70vh]"
      >
        <img
          src={post.heroImage}
          alt={post.title}
          loading="eager"
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10" />
        <div className="absolute bottom-0 left-0 right-0 p-8 md:p-16 max-w-4xl" style={{ textShadow: '0 2px 12px rgba(0,0,0,0.8), 0 1px 4px rgba(0,0,0,0.5)' }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <span className="inline-block px-3 py-1 bg-[#C5A059] text-white text-[10px] font-bold uppercase tracking-[0.15em] rounded-full mb-5">
              {post.category}
            </span>
            <h1 className="font-display text-3xl md:text-5xl lg:text-6xl text-white tracking-tight leading-tight">
              {post.title}
            </h1>
          </motion.div>
        </div>
      </motion.section>

      {/* Breadcrumb & Author Bar */}
      <section className="max-w-3xl mx-auto px-6 pt-10">
        {/* Breadcrumb */}
        <motion.nav
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="flex items-center gap-2 text-[11px] text-[#1A1A1A]/40 mb-8"
        >
          <button onClick={() => navigate('/')} className="hover:text-[#C5A059] transition-colors">Home</button>
          <span>/</span>
          <button onClick={() => navigate('/field-notes')} className="hover:text-[#C5A059] transition-colors">Field Notes</button>
          <span>/</span>
          <span className="text-[#1A1A1A]/70 truncate">{post.title}</span>
        </motion.nav>

        {/* Author Bar */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="flex flex-wrap items-center justify-between gap-4 pb-8 mb-10 border-b border-black/10"
        >
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-[#C5A059]/20 flex items-center justify-center text-[#C5A059] font-display text-sm">
              {post.author.split(' ').map((n) => n[0]).join('')}
            </div>
            <div>
              <p className="text-sm font-semibold">{post.author}</p>
              <p className="text-[11px] text-[#1A1A1A]/50">{post.authorRole}</p>
            </div>
          </div>
          <div className="flex items-center gap-4 text-[11px] text-[#1A1A1A]/50">
            <span>{formatDate(post.date)}</span>
            <span className="w-1 h-1 rounded-full bg-current" />
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {post.readTime}
            </span>
          </div>
        </motion.div>
      </section>

      {/* Article Content */}
      <motion.article
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.6 }}
        className="max-w-3xl mx-auto px-6 pb-16"
      >
        <div
          className="prose-field-notes"
          dangerouslySetInnerHTML={{ __html: post.content }}
          onClick={handleContentClick}
        />
      </motion.article>

      {/* Share */}
      <section className="max-w-3xl mx-auto px-6 pb-16">
        <div className="flex items-center gap-4 pt-8 border-t border-black/10">
          <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-[#1A1A1A]/40 flex items-center gap-2">
            <Share2 className="w-3.5 h-3.5" /> Share
          </span>
          <button
            onClick={handleCopyLink}
            className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#1A1A1A]/5 text-[11px] font-medium hover:bg-[#C5A059]/20 hover:text-[#C5A059] transition-all duration-300"
          >
            {copied ? <Check className="w-3.5 h-3.5" /> : <Link2 className="w-3.5 h-3.5" />}
            {copied ? 'Copied' : 'Copy Link'}
          </button>
          <button
            onClick={handleTwitterShare}
            className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#1A1A1A]/5 text-[11px] font-medium hover:bg-[#C5A059]/20 hover:text-[#C5A059] transition-all duration-300"
          >
            <Twitter className="w-3.5 h-3.5" />
            Twitter
          </button>
        </div>
      </section>

      {/* Back to Field Notes */}
      <section className="max-w-3xl mx-auto px-6 pb-12">
        <button
          onClick={() => navigate('/field-notes')}
          className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.15em] text-[#C5A059] hover:text-[#D4AF37] transition-colors duration-300"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
          Back to Field Notes
        </button>
      </section>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="bg-[#1A1A1A] py-20 px-6">
          <div className="max-w-6xl mx-auto">
            <h2 className="font-display text-2xl md:text-3xl text-white tracking-tight mb-12">
              Continue Reading
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
              {relatedPosts.map((related) => (
                <motion.article
                  key={related.slug}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                  onClick={() => navigate(`/field-notes/${related.slug}`)}
                  className="group cursor-pointer"
                >
                  <div className="relative rounded-xl overflow-hidden aspect-[4/3] mb-6">
                    <img
                      src={related.heroImage}
                      alt={related.title}
                      loading="lazy"
                      onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.parentElement!.classList.add('bg-gradient-to-br', 'from-[#2a2a2a]', 'to-[#1a1a1a]'); }}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    <span className="absolute top-4 left-4 px-3 py-1 bg-white/10 backdrop-blur-sm text-white text-[10px] font-bold uppercase tracking-[0.15em] rounded-full">
                      {related.category}
                    </span>
                  </div>
                  <h3 className="font-display text-lg text-white tracking-tight mb-2 group-hover:text-[#C5A059] transition-colors duration-300">
                    {related.title}
                  </h3>
                  <p className="text-sm text-white/50 line-clamp-2 font-serif italic mb-4">
                    {related.excerpt}
                  </p>
                  <span className="flex items-center gap-1.5 text-[#C5A059] text-[11px] font-semibold uppercase tracking-[0.15em] opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    Read Story <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </motion.article>
              ))}
            </div>
          </div>
        </section>
      )}
      {/* Product CTA */}
      <section className="py-20 px-4 sm:px-6 md:px-12 bg-[#1A1A1A] border-t border-[#1A1A1A]/10">
        <div className="max-w-2xl mx-auto text-center">
          <span className="font-sans text-[10px] uppercase tracking-[0.4em] text-[#C5A059] mb-4 block">Design Your Own</span>
          <h2 className="font-display text-2xl md:text-3xl text-white tracking-tight mb-4">
            Inspired? Create your itinerary.
          </h2>
          <p className="font-serif text-base text-white/50 italic mb-8 max-w-lg mx-auto leading-relaxed">
            Generate a magazine-quality, AI-curated travel itinerary in under 90 seconds.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/"
              className="px-10 py-4 bg-[#C5A059] text-black font-sans text-[10px] uppercase tracking-[0.3em] font-bold hover:bg-white transition-all duration-500"
            >
              Start Planning
            </Link>
            <Link
              to="/pricing"
              className="px-10 py-4 border border-white/10 text-white font-sans text-[10px] uppercase tracking-[0.3em] hover:border-[#C5A059]/50 hover:text-[#C5A059] transition-all duration-500"
            >
              View Plans
            </Link>
          </div>
        </div>
      </section>
      <SiteFooter />
    </div>
  );
};

export default JourneyPostPage;
